import { ButtonUnstyled } from '@emico-react/buttons'
import {
  NextIcon as BaseNextIcon,
  PreviousIcon as BasePreviousIcon,
} from '@emico-react/icons'
import { overrideOptional } from '@emico-utils/overrides'
import styled from '@emotion/styled'
import type { PressEvent } from '@react-types/shared'
import {
  ReactNode,
  useRef,
  useCallback,
  useEffect,
  useState,
  ComponentProps,
  Children,
} from 'react'

import { SliderDots } from './SliderDots'

const Slider = styled.div`
  position: relative;
`

type TrackProps = Pick<Props, 'padding'>

export const Track = styled('div', {
  shouldForwardProp: (prop) => !['padding'].includes(prop.toString()),
})<TrackProps>`
  display: grid;
  overflow-x: scroll;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  -ms-overflow-style: none;
  scrollbar-width: none;
  scroll-padding: var(
    --emico-component-slider-padding,
    0 ${({ padding }) => padding}px
  );
  height: 100%;

  &::-webkit-scrollbar {
    display: none;
  }
`

type SlideProps = Pick<
  Props,
  'snapAlign' | 'slideGap' | 'padding' | 'slidesToShow'
> & {
  dividedSlideGap: number
}

export const Slides = styled('div', {
  shouldForwardProp: (prop) =>
    ![
      'snapAlign',
      'slideGap',
      'padding',
      'slidesToShow',
      'dividedSlideGap',
    ].includes(prop.toString()),
})<SlideProps>`
  display: grid;
  grid-auto-columns: ${({ slidesToShow, dividedSlideGap }) =>
    slidesToShow &&
    (slidesToShow === 1
      ? '100%'
      : `calc(100% / ${slidesToShow} - ${dividedSlideGap}px)`)};
  gap: var(--emico-component-slider-gap, ${({ slideGap }) => slideGap}px);
  grid-auto-flow: column;
  padding: var(
    --emico-component-slider-padding,
    0 ${({ padding }) => padding}px
  );
  height: 100%;
  justify-content: left;

  & > * {
    scroll-snap-align: ${({ snapAlign }) => snapAlign};
    overflow: hidden;
  }
`

export const ArrowButton = overrideOptional(styled(ButtonUnstyled)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
`)

export const PrevButton = overrideOptional(styled(ArrowButton)`
  left: 0;
`)

export const NextButton = overrideOptional(styled(ArrowButton)`
  right: 0;
`)

export const SliderPrevIcon = overrideOptional(styled(BasePreviousIcon)`
  width: 2em;
  height: 2em;
`)

export const SliderNextIcon = overrideOptional(styled(BaseNextIcon)`
  width: 2em;
  height: 2em;
`)

interface ArrowProps {
  handleClick: (
    e: PressEvent | React.MouseEvent<HTMLElement, MouseEvent>,
  ) => void
  isDisabled: boolean
}

interface Props {
  children: ReactNode
  currentSlide?: number
  showDots?: boolean
  showArrows?: boolean
  slideGap?: number
  padding?: number
  snapAlign?: string
  slidesToShow?: number
  onSlideChange?(index: number): void
  onSlideMove?(offset: number): void
  renderPrevArrow?: (props: ArrowProps) => ReactNode
  renderNextArrow?: (props: ArrowProps) => ReactNode
  renderSliderDots?: (props: ComponentProps<typeof SliderDots>) => ReactNode
}

export const ComponentSlider = overrideOptional(
  ({
    children,
    currentSlide = 0,
    showDots = false,
    showArrows = false,
    slideGap = 0,
    padding = 0,
    snapAlign = 'center',
    slidesToShow,
    onSlideChange,
    onSlideMove,
    renderPrevArrow = ({ handleClick, isDisabled }) => (
      <PrevButton
        isDisabled={isDisabled}
        onPress={handleClick}
        analyticsContext="componentSlider"
        analyticsName="prevButton"
      >
        <SliderPrevIcon />
      </PrevButton>
    ),
    renderNextArrow = ({ handleClick, isDisabled }) => (
      <NextButton
        isDisabled={isDisabled}
        onPress={handleClick}
        analyticsContext="componentSlider"
        analyticsName="nextButton"
      >
        <SliderNextIcon />
      </NextButton>
    ),
    renderSliderDots = ({ slides, currentSlide, onDotClick }) => (
      <SliderDots
        slides={slides}
        currentSlide={currentSlide}
        onDotClick={onDotClick}
      />
    ),
    ...other
  }: Props) => {
    const slideCount = Children.count(children)
    const trackRef = useRef<HTMLDivElement>(null)
    const slidesRef = useRef<HTMLDivElement>(null)
    const sliderWidth = slidesRef.current?.clientWidth
    const [slideState, setSlideState] = useState<number>(currentSlide)
    const [disableNext, setDisableNext] = useState(slideCount === 1)
    const [disablePrev, setDisablePrev] = useState(currentSlide === 0)
    const lastSlideChange = useRef(0)

    // Visible slide gaps, divided over slidesToShow
    const dividedSlideGap = slidesToShow
      ? ((Math.ceil(slidesToShow) - 1) * slideGap) / Math.ceil(slidesToShow)
      : slideGap

    const handleSliderChange = useCallback(
      (index: number, animate: boolean = true) => {
        if (
          !trackRef.current ||
          !slidesRef.current ||
          index < 0 ||
          index >= slideCount ||
          // If the new index is already sent using the onSlideChange callback
          // this might cause a loop. So we check the last slide change is the
          // new index
          lastSlideChange.current === index
        ) {
          return
        }

        const slideWidth =
          slidesToShow && slidesToShow > 1 && sliderWidth
            ? sliderWidth / slidesToShow + slideGap
            : slidesRef.current.children[0].clientWidth + slideGap

        const left =
          index === slideCount - 1
            ? trackRef.current.scrollWidth
            : slideWidth * index

        trackRef.current.scrollTo({
          left,
          behavior: animate ? 'smooth' : 'auto',
        })
      },
      [slideCount, slideGap, sliderWidth, slidesToShow],
    )

    useEffect(() => {
      setDisableNext(slideCount === 1)
    }, [slideCount])

    useEffect(() => {
      onSlideChange?.(slideState)
      lastSlideChange.current = slideState
    }, [onSlideChange, slideState])

    useEffect(() => {
      handleSliderChange(currentSlide)
    }, [handleSliderChange, currentSlide])

    // Check if the currentSlide is not 0 initially
    useEffect(() => {
      if (currentSlide > 0 && slidesRef.current && trackRef.current) {
        lastSlideChange.current = -1
        handleSliderChange(currentSlide, false)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slidesRef, trackRef])

    const onScroll = useCallback(() => {
      if (!trackRef.current || !slidesRef.current?.children[0]) {
        return
      }

      const slider = trackRef.current
      const slides = slidesRef.current
      const slideWidth = slides.children[0].clientWidth + slideGap
      const offset = slider.scrollLeft / slideWidth
      const index = Math.round(offset)
      const mathRoundScrollLeft = Math.round(slider.scrollLeft)

      setSlideState(index)
      setDisableNext(
        mathRoundScrollLeft === slider.scrollWidth - slider.clientWidth ||
          mathRoundScrollLeft === slider.scrollWidth - slider.clientWidth + 1,
      )
      setDisablePrev(mathRoundScrollLeft === 0)
      onSlideMove?.(offset)
    }, [onSlideMove, slideGap])

    return (
      <Slider {...other}>
        <Track ref={trackRef} padding={padding} onScroll={onScroll}>
          <Slides
            ref={slidesRef}
            snapAlign={snapAlign}
            slideGap={slideGap}
            padding={padding}
            slidesToShow={slidesToShow}
            dividedSlideGap={dividedSlideGap}
          >
            {children}
          </Slides>
        </Track>

        {showArrows &&
          renderNextArrow({
            handleClick: () => handleSliderChange(slideState + 1),
            isDisabled: disableNext,
          })}

        {showArrows &&
          renderPrevArrow({
            handleClick: () => handleSliderChange(slideState - 1),
            isDisabled: disablePrev,
          })}

        {showDots &&
          renderSliderDots({
            slides: slideCount,
            currentSlide: slideState,
            onDotClick: handleSliderChange,
          })}
      </Slider>
    )
  },
)
